import { withStyles, Box, PulloverSelect } from 'vopay';

import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useFetchAccountBillingSettings from '@/hooks/useApi/useFetchAccountBillingSettings';
import usePartnerBearer from '@/components/Entities/hooks/useApi/usePartnerBearer';
import AccountContext from '@/components/Entities/Accounts/AccountContext';
import BillingPackageDetails from '@/components/Accounts/Billing/BillingPackageDetails/BillingPackageDetails';

import styles from './Billing.module.scss';

const cn = withStyles(styles);

const Billing = () => {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Billing' });
  const { accountId } = useParams<{ accountId: string }>();
  const { details } = useContext(AccountContext);
  const partnerBearer = usePartnerBearer(details[accountId]?.PartnerAccountID ?? '');
  const [accountBillingSettings, getAccountBillingSettings] = useFetchAccountBillingSettings({ bearer: partnerBearer });
  const [selectedId, setSelectedId] = useState('');

  useEffect(() => {
    if (!partnerBearer) return;
    getAccountBillingSettings(accountId);
  }, [partnerBearer]);

  useEffect(() => {
    if (accountBillingSettings.length > 0 && !selectedId) {
      const active = accountBillingSettings.find((s) => s.IsActive);
      setSelectedId((active ?? accountBillingSettings[0]).ID);
    }
  }, [accountBillingSettings]);

  const selected = accountBillingSettings.find((s) => s.ID === selectedId);

  const endDate =
    selected?.EndDate === '9999-12-31' || selected?.EndDate === null
      ? t('currentEndDate', { defaultValue: 'Current' })
      : selected?.EndDate;

  return (
    <Box flx="col" gapSize="16px">
      <div className={cn('packageRow')}>
        <span className={cn('packageLabel')}>{t('packageLabel', { defaultValue: 'Package' })}</span>

        <PulloverSelect name="billingPackage" value={selectedId} onChange={setSelectedId}>
          {accountBillingSettings.map((s) => (
            <PulloverSelect.Option key={s.ID} value={s.ID}>
              {s.Currency} - {s.PackageName}
            </PulloverSelect.Option>
          ))}
        </PulloverSelect>
      </div>

      {selected && (
        <div className={cn('dates')}>
          <span>
            {t('startDate', { defaultValue: 'Start Date' })}{' '}
            <span className={cn('dateValue')}>{selected.StartDate}</span>
          </span>

          <span className={cn('dateSeparator')}>|</span>

          <span>
            {t('endDate', { defaultValue: 'End Date' })} <span className={cn('dateValue')}>{endDate}</span>
          </span>
        </div>
      )}

      {selectedId && <BillingPackageDetails packageId={selectedId} bearer={partnerBearer} />}
    </Box>
  );
};

export default Billing;
